import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import * as StripeJs from "@stripe/stripe-js";
import { NextRouter, useRouter } from "next/router";
import React, {
  StrictMode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Stripe } from "stripe";
import styled from "styled-components";

import PaymentsClient from "@/client/PaymentsClient";
import Button from "@/components/Button";
import Loading from "@/components/Loading";
import * as U from "@/components/Utils";
import configClient from "@/config.client";
import { CheckoutSessionStatusResponse } from "@/types/Payments";
import { Device } from "@/utils/device";
import * as UserContext from "components/UserContext";

const paymentsClient = new PaymentsClient();
const stripePromise = StripeJs.loadStripe(configClient.STRIPE_PUBLIC);

const Pay: React.FC = () => {
  const { router, status, checkoutSession, onComplete } = useMembershipsPay();

  return (
    <StrictMode>
      <S.Container>
        {status === "showLoading" && <LoadingSpinner />}
        {status === "showForm" && (
          <StripeForm
            client_secret={checkoutSession.client_secret}
            onComplete={onComplete}
            navigate={router.push}
            returnUrl={router?.query.returnUrl}
          />
        )}
      </S.Container>
    </StrictMode>
  );
};

function LoadingSpinner() {
  return (
    <S.Section>
      <Loading />
    </S.Section>
  );
}

const StripeForm = React.memo(function StripeForm({
  client_secret,
  onComplete,
  navigate,
  returnUrl,
}: {
  client_secret: string;
  onComplete: () => void;
  navigate: NextRouter["push"];
  returnUrl: string | string[];
}) {
  return (
    <S.Section>
      <U.Space height={20} />
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          clientSecret: client_secret,
          onComplete: onComplete,
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
      <U.Space height={20} />
      <S.CenterBox>
        <Button
          onClick={() => navigate(returnUrl as unknown as string)}
          isSecondary
          text="Back"
        />
      </S.CenterBox>
    </S.Section>
  );
});

const useMembershipsPay = () => {
  const router = useRouter();
  const userInfo = useContext(UserContext.Context);
  const [checkoutSession, setCheckoutSession] =
    useState<Stripe.Checkout.Session | null>();
  const [checkoutSessionStatus, setCheckoutSessionStatus] =
    useState<CheckoutSessionStatusResponse | null>();
  const [requestStatus, setRequestStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  const status = useMemo<
    "showLoading" | "showForm" | "showSuccess" | "showError"
  >(() => {
    if (Boolean(checkoutSession) === false) return "showLoading";
    if (Boolean(checkoutSession) && Boolean(checkoutSessionStatus) === false)
      return "showForm";
    if (checkoutSessionStatus?.status === "paid") return "showSuccess";
    if (checkoutSessionStatus?.status === "unpaid") return "showError";
  }, [checkoutSession, checkoutSessionStatus]);

  const createCheckoutSession = useCallback(async () => {
    if (router.query?.type && requestStatus === "idle") {
      setRequestStatus("loading");
      const data = await paymentsClient.createCheckoutSession({
        priceLookupId: router.query?.type as unknown as string,
        mode: "payment",
        customerEmail: userInfo?.email,
      });
      setRequestStatus("success");
      setCheckoutSession(data);
    }
  }, [router.query]);

  const navigateToSuccessPage = useCallback(() => {
    if (status === "showSuccess") {
      router.push(`/memberships/success?session_id=${checkoutSession.id}`);
    }
  }, [status]);

  React.useEffect(() => {
    createCheckoutSession();
  }, [createCheckoutSession]);

  React.useEffect(() => {
    navigateToSuccessPage();
  }, [navigateToSuccessPage]);

  const onComplete = async () => {
    const data = await paymentsClient.getCheckoutSessionStatus(
      checkoutSession.id
    );
    setCheckoutSessionStatus(data);
  };

  return { router, status, checkoutSession, onComplete };
};

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: row;

    @media (max-width: ${Device.tablet}) {
      flex-direction: column;
    }
  `,

  Section: styled.div`
    width: 100%;
  `,

  CenterBox: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  TextWrap: styled.div`
    padding: 0 20px;
  `,
};

export default Pay;
